/**
 * Klaviyo — client-side web tracking for headless storefront.
 *
 * The Klaviyo ↔ Shopify native integration handles checkout abandonment,
 * placed orders, and recovery flows server-side. This module adds the
 * client-side events that the native integration cannot see on a headless
 * site: Viewed Product, Added to Cart, and Started Checkout.
 *
 * The snippet (klaviyo.js) is loaded in index.html. This module wraps the
 * _learnq push API into typed functions matching the other pixel modules.
 */

import { hasOptedOut } from "./consent";
import { isProductionHost } from "./is-production-host";
const KLAVIYO_SITE_ID = "QRyR3D";

declare global {
  interface Window {
    _learnq?: { push: (args: unknown[]) => void };
    klaviyo?: {
      identify: (props: Record<string, unknown>) => void;
      track: (event: string, props?: Record<string, unknown>) => void;
    };
  }
}

let initialized = false;

/**
 * Load the Klaviyo snippet (idempotent). Called via requestIdleCallback
 * in AppShell so it never blocks the main thread or first paint.
 */
export function initKlaviyo() {
  if (initialized || typeof window === "undefined" || !isProductionHost()) return;
  initialized = true;

  if (hasOptedOut()) return;

  // If the snippet from index.html already ran, _learnq exists.
  if (window._learnq) return;

  window._learnq = window._learnq || [];
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://static.klaviyo.com/onsite/js/${KLAVIYO_SITE_ID}/klaviyo.js`;
  document.head.appendChild(script);
}

// ─── Client-side tracked events ─────────────────────────────────────

export function klaviyoTrackViewedProduct(params: {
  product_id: string;
  product_name: string;
  price: number;
  currency: string;
  url?: string;
}) {
  if (typeof window === "undefined") return;
  const q = window._learnq || window.klaviyo;
  if (!q) return;

  const props = {
    ProductID: params.product_id,
    Name: params.product_name,
    Price: params.price,
    Currency: params.currency,
    URL: params.url || window.location.href,
  };

  if (window.klaviyo) {
    window.klaviyo.track("Viewed Product", props);
  } else if (window._learnq) {
    window._learnq.push(["track", "Viewed Product", props]);
  }
}

export function klaviyoTrackAddedToCart(params: {
  product_id: string;
  product_name: string;
  price: number;
  quantity: number;
  currency: string;
}) {
  if (typeof window === "undefined") return;
  if (hasOptedOut()) return;

  const props = {
    ProductID: params.product_id,
    Name: params.product_name,
    Price: params.price,
    Quantity: params.quantity,
    Currency: params.currency,
    Value: params.price * params.quantity,
  };

  if (window.klaviyo) {
    window.klaviyo.track("Added to Cart", props);
  } else if (window._learnq) {
    window._learnq.push(["track", "Added to Cart", props]);
  }
}

export function klaviyoTrackStartedCheckout(params: {
  value: number;
  currency: string;
  item_count: number;
  items: Array<{
    product_id: string;
    product_name: string;
    price: number;
    quantity: number;
  }>;
}) {
  if (typeof window === "undefined") return;
  if (hasOptedOut()) return;

  const props = {
    Value: params.value,
    Currency: params.currency,
    ItemCount: params.item_count,
    Items: params.items.map((i) => ({
      ProductID: i.product_id,
      Name: i.product_name,
      Price: i.price,
      Quantity: i.quantity,
    })),
  };

  if (window.klaviyo) {
    window.klaviyo.track("Started Checkout", props);
  } else if (window._learnq) {
    window._learnq.push(["track", "Started Checkout", props]);
  }
}